import { Component, OnInit } from '@angular/core';
import { OrganisationsService } from '../../services';
import { Message } from '../../classes/Message';
import { OrderDiscountCode, OrderDiscountCodeEdit } from '../../interfaces/order-discount-code';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { compare } from 'fast-json-patch';
import moment from 'moment';
import { DiscountCodeService, DiscountCodeSettings } from '../../services/discount-code.service';
import { InputHelper } from '../../utils/input.helper';
import { MessageService, SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { IOrderDiscountCodeFilter } from '../../interfaces/order-discount-code-filter';
import { debounceTime } from 'rxjs/operators';

enum DiscountCodeMode {
  Percent = 0,
  Amount = 1,
}

function percentageDiscountValidator(
  component: ManageB2CDiscountComponent,
  discountCodeMode: DiscountCodeMode,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = Number(control.value);

    if (component.discountCodeMode !== DiscountCodeMode.Percent) {
      return null;
    }

    const aboveMinimum = controlValue >= component.settings.minimumDiscountCodePercentage;
    const belowMaximum = controlValue <= component.settings.maximumDiscountCodePercentage;

    if (aboveMinimum && belowMaximum) {
      return null;
    }

    return {
      withinRange: true,
    };
  };
}

function amountDiscountValidator(
  component: ManageB2CDiscountComponent,
  discountCodeMode: DiscountCodeMode,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = Number(control.value);

    if (component.discountCodeMode !== DiscountCodeMode.Amount) {
      return null;
    }

    const aboveMinimum = controlValue >= component.settings.minimumDiscountCodeAmount;
    const belowMaximum = controlValue <= component.settings.maximumDiscountCodeAmount;

    if (aboveMinimum && belowMaximum) {
      return null;
    }
    return {
      withinRange: true,
    };
  };
}

@Component({
  selector: 'manage-b2c-discount',
  templateUrl: './manage-b2c-discount.component.html',
  styleUrls: ['./manage-b2c-discount.component.scss'],
})
export class ManageB2CDiscountComponent implements OnInit {
  public orderDiscountCodes: OrderDiscountCodeEdit[] = [];
  selectedDiscount: OrderDiscountCode;
  private beforeEditFormValues: {} = {};

  public DiscountCodeMode = DiscountCodeMode;
  public filter: Subject<IOrderDiscountCodeFilter>;
  public filterVal: IOrderDiscountCodeFilter;

  constructor(
    private discountCodeService: DiscountCodeService,
    private organisationService: OrganisationsService,
    private messageService: MessageService,
    public inputHelper: InputHelper,
  ) {}

  editForm = new FormGroup({
    id: new FormControl(),
    organisationId: new FormControl(),
    code: new FormControl(null),
    description: new FormControl(null),
    urgent: new FormControl(true),
    nonUrgent: new FormControl(true),
    discountPercentage: new FormControl(null, [
      Validators.min(0),
      Validators.max(100),
      percentageDiscountValidator(this, DiscountCodeMode.Percent),
    ]),
    discountAmount: new FormControl(null, [
      Validators.min(0),
      amountDiscountValidator(this, DiscountCodeMode.Amount),
    ]),
    startDate: new FormControl(null),
    startDateDisplay: new FormControl(null),
    endDate: new FormControl(null),
    endDateDisplay: new FormControl(null),
    maxUses: new FormControl(9999, [Validators.min(0)]),
    added: new FormControl(null),
    updated: new FormControl(null),
  });

  showEditDiscountDialog = false;
  customerOptions: any[] = [];
  loading = true;
  filterCustomer = null;
  discountCodeMode: DiscountCodeMode = DiscountCodeMode.Percent;

  settings: DiscountCodeSettings = {
    minimumDiscountCodePercentage: 0,
    maximumDiscountCodePercentage: 100,
    minimumDiscountCodeAmount: 1,
    maximumDiscountCodeAmount: 20,
  };

  discountModeOptions: SelectItem[] = [
    { label: 'Percent', value: DiscountCodeMode.Percent },
    { label: 'Amount', value: DiscountCodeMode.Amount },
  ];

  ngOnInit() {
    this.initDefaultFilter();

    this.filter = new Subject<IOrderDiscountCodeFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.getDiscountCodes(f);
      this.filterVal = f;
    });
    this.filter.next(this.filterVal);

    this.getCustomers();
    this.getSettings();
  }

  getDiscountCodes(filter: IOrderDiscountCodeFilter) {
    this.loading = true;
    this.discountCodeService
      .getDiscountCodes(filter)
      .then((res) => {
        this.orderDiscountCodes = res;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getSettings() {
    this.discountCodeService.getDiscountCodeSettings().then((res) => {
      this.settings = res;
    });
  }

  discountSelected(discount: OrderDiscountCode) {
    this.selectedDiscount = discount;
  }

  get f() {
    return this.editForm.value;
  }

  initCreateDiscount() {
    this.editForm.reset();
    this.storeFormValues();
    this.showEditDiscountDialog = true;
  }

  initEditDiscount(discountCode: OrderDiscountCode) {
    this.editForm.reset();

    this.discountCodeMode = discountCode.discountAmount
      ? DiscountCodeMode.Amount
      : DiscountCodeMode.Percent;

    this.editForm.patchValue({
      id: discountCode.id,
      organisationId: discountCode.organisationId,
      code: discountCode.code,
      urgent: discountCode.urgent,
      nonUrgent: discountCode.nonUrgent,
      description: discountCode.description,
      discountPercentage: discountCode.discountPercentage,
      discountAmount: discountCode.discountAmount,
      startDate: discountCode.startDate,
      startDateDisplay: new Date(discountCode.startDate),
      endDate: discountCode.endDate,
      endDateDisplay: new Date(discountCode.endDate ?? '2050-12-31'),
      maxUses: discountCode.maxUses,
      added: discountCode.added,
      updated: discountCode.updated,
    });
    this.storeFormValues();
    this.showEditDiscountDialog = true;
  }

  storeFormValues() {
    this.beforeEditFormValues = Object.assign({}, this.editForm.value);
  }

  saveDiscount() {
    this.editForm.patchValue({
      startDate: this.f.startDateDisplay
        ? moment(this.f.startDateDisplay).format('YYYY-MM-DD')
        : null,
      endDate: this.f.endDateDisplay ? moment(this.f.endDateDisplay).format('YYYY-MM-DD') : null,
    });

    this.editForm.patchValue({
      discountPercentage: Number(this.f.discountPercentage),
      discountAmount: Number(this.f.discountAmount),
    });

    if (this.f.id == null) {
      this.discountCodeService
        .createDiscountCode({
          code: this.f.code,
          organisationId: this.f.organisationId,
          description: this.f.description,
          urgent: this.f.urgent,
          nonUrgent: this.f.nonUrgent,
          discountPercentage: this.f.discountPercentage,
          discountAmount: this.f.discountAmount,
          startDate: this.f.startDate,
          endDate: this.f.endDate,
          maxUses: this.f.maxUses,
        })
        .then(() => {
          this.showEditDiscountDialog = false;
          this.getDiscountCodes(this.filterVal);
        })
        .catch((e) =>
          this.messageService.add(Message.Error('Discount code already existed', 'Error')),
        );
    } else {
      let patch = compare(this.beforeEditFormValues, this.editForm.value);

      this.discountCodeService
        .patchDiscountCode(this.f.id, patch)
        .then(() => {
          this.showEditDiscountDialog = false;
          this.getDiscountCodes(this.filterVal);
        })
        .catch((e) =>
          this.messageService.add(Message.Error('Discount code already existed', 'Error')),
        );
    }
  }

  private getCustomers() {
    this.organisationService.getAllCustomersLookup().then((res) => {
      res.unshift({ value: null, label: 'All Customers' });
      this.customerOptions = res;
    });
  }

  filtered(orderDiscountCodes: OrderDiscountCodeEdit[]): OrderDiscountCodeEdit[] {
    return orderDiscountCodes.filter((x) => {
      if (this.filterCustomer == null) return true;
      return x.organisationId === this.filterCustomer;
    });
  }

  asDiscountCode(item: any): OrderDiscountCode {
    return item as OrderDiscountCode;
  }

  discountCodeModeChange(e) {
    if (this.discountCodeMode === DiscountCodeMode.Percent) {
      this.editForm.controls.discountAmount.setValue(null);
    } else {
      this.editForm.controls.discountPercentage.setValue(null);
    }
  }

  deleteDiscountCode() {}

  initDefaultFilter() {
    this.filterVal = {
      sortField: 'Code', // ASC
      sortOrder: 1,
      filters: {
        code: { value: null },
      },
    };
  }

  clearFilter() {
    this.initDefaultFilter();
    this.filter.next(this.filterVal);
  }

  search() {
    this.filterVal.first = 0;
    this.filter.next(this.filterVal);
  }
}
